import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Button, EmptyState, Input } from '@/components/ui';
import { cx } from '@/lib/cx';
import {
  guideSearchText,
  guideSectionIdForPath,
  guideSectionsFor,
  type GuideBlock,
  type GuideSection,
} from '@/lib/guide-content';
import { useAuth } from '@/lib/use-auth';
import { useBodyScrollLock } from '@/lib/use-sidebar';

/*
 * The in-app guide: a `?` in the top bar that opens a right-side drawer.
 *
 * ⚠️ **THE NO-HELPER-TEXT RULE DOES NOT COVER THIS, AND THAT IS NOT AN
 * EXEMPTION — IT IS A DIFFERENT THING.** The standing prohibition is on prose
 * ATTACHED TO A CONTROL: a `hint` under a field, a `description` on a `Modal`,
 * a sentence explaining a button. That copy competes with the control it sits
 * beside and rots against it silently. This is a DESTINATION somebody opens on
 * purpose, reads, and closes. The same reasoning is written out at the top of
 * `@/lib/guide-content`, which is where the sentences themselves live.
 *
 * ⚠️ **The rule still binds INSIDE this drawer.** Its own controls carry no
 * helper text: the filter box has a placeholder and nothing else, and the
 * section headers are labels. Being a guide is not a licence to explain the
 * guide.
 *
 * ⚠️ **It shows only the sections this reader's access level offers**, so
 * nobody is taught a page they cannot open. That decision is `guideSectionsFor`
 * in `@/lib/guide-content`, over the same `ability` the sidebar and the command
 * palette consult; it degrades OPEN, and three sections carry no gate at all so
 * the drawer can never come back empty. Read that function's header first.
 *
 * Everything below the shell is copied from `@/components/tasks/ActivityDrawer`
 * on purpose — that is the house slide-over and its focus behaviour was hard
 * won. A second, subtly different overlay is how two dialogs in one app end up
 * disagreeing about Escape.
 */

/* --- icons --------------------------------------------------------------- */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function QuestionIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <circle cx="12" cy="12" r="9.25" />
      <path d="M9.6 9.2a2.5 2.5 0 1 1 3.3 2.37c-.55.2-.9.73-.9 1.32v.61" />
      <path d="M12 16.6h.01" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  );
}

function DisclosureIcon({ open }: { open: boolean }) {
  return (
    <svg
      {...stroke}
      className={cx(
        'h-4 w-4 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-90',
      )}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

/** Sits inside a `note`, so overdue-style: the tone is never the only signal. */
function NoteGlyph() {
  return (
    <svg {...stroke} strokeWidth={2} className="mt-px h-3.5 w-3.5 shrink-0">
      <path d="M12 8v4.5" />
      <path d="M12 16.5h.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

/* --- the body blocks ----------------------------------------------------- */

const NOTE_TONE: Record<'warning' | 'info', string> = {
  warning: 'border-warning-border bg-warning-soft text-warning',
  info: 'border-info-border bg-info-soft text-info',
};

function GuideBlockView({ block }: { block: GuideBlock }): ReactElement {
  switch (block.kind) {
    case 'heading':
      return <h4 className="mt-1 text-xs font-semibold text-fg-secondary">{block.text}</h4>;
    case 'paragraph':
      return <p className="text-xs leading-5 text-fg-muted">{block.text}</p>;
    case 'list':
      return (
        <ul className="flex list-disc flex-col gap-1 pl-4 text-xs leading-5 text-fg-muted marker:text-fg-subtle">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case 'note':
      /*
       * A note is never carried by its colour alone — the glyph is the second
       * signal, the same rule the overdue badge follows.
       */
      return (
        <p
          className={cx(
            'flex gap-2 rounded-md border px-2.5 py-2 text-xs leading-5',
            NOTE_TONE[block.tone],
          )}
        >
          <NoteGlyph />
          <span className="min-w-0">{block.text}</span>
        </p>
      );
  }
}

/* --- one section --------------------------------------------------------- */

function GuideSectionView({
  section,
  open,
  onToggle,
}: {
  section: GuideSection;
  open: boolean;
  onToggle: () => void;
}): ReactElement {
  const bodyId = useId();

  return (
    <section className="border-b border-border last:border-b-0">
      <h3 className="m-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          leadingIcon={<DisclosureIcon open={open} />}
          className="h-auto w-full justify-start rounded-none px-4 py-3 text-left text-sm font-semibold text-fg max-sm:h-auto"
        >
          <span className="min-w-0 flex-1 text-left">{section.title}</span>
        </Button>
      </h3>

      {open && (
        <div id={bodyId} className="flex flex-col gap-2.5 px-4 pt-0.5 pb-4">
          {section.body.map((block, index) => (
            <GuideBlockView key={index} block={block} />
          ))}
        </div>
      )}
    </section>
  );
}

/* --- the drawer ---------------------------------------------------------- */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The `?` in the top bar and the sheet behind it, in one component, because the
 * trigger is what focus goes back to when the sheet closes — splitting them
 * would mean threading a ref through `AppShell` for no gain.
 */
export function GuideDrawer(): ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<readonly string[]>([]);

  const titleId = useId();
  const filterId = useId();

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { pathname } = useLocation();
  const { ability } = useAuth();

  /*
   * Nobody is taught a page their access level does not offer them.
   *
   * ⚠️ **The decision lives in `guideSectionsFor`, not here** — it is a pure
   * function over the same `ability` the sidebar and the command palette
   * consult, so the three surfaces cannot disagree about who is offered what.
   * Read its header before changing anything about this.
   *
   * ⚠️ It DEGRADES OPEN: an unloaded or failed permission read leaves every
   * section in place. A guide that emptied itself while permissions arrived
   * would read as an app that is broken.
   */
  const sections = useMemo(() => guideSectionsFor(ability), [ability]);

  const close = useCallback(() => setOpen(false), []);

  useBodyScrollLock(open);

  /*
   * It opens where you are.
   *
   * Resolved HERE, in the click, rather than in an effect on `open`. Two
   * reasons and only one of them is style: an effect that sets state on mount
   * is what `react(set-state-in-effect)` forbids, and opening is a user action
   * with the current path already in hand — there is nothing to synchronise.
   *
   * Resolving on OPEN rather than on every navigation is also what stops a
   * section somebody expanded by hand from closing under them when they follow
   * a link with the drawer up.
   *
   * ⚠️ Nothing is persisted, deliberately. This is a derived starting point,
   * not a preference: a remembered section would open on last week's page.
   */
  function openDrawer() {
    /*
     * ⚠️ **The path's own section may not be one this reader is offered**, so
     * it is checked against `sections` rather than trusted. That happens on a
     * page whose section is gated more narrowly than the page itself — and
     * expanding a section that is not rendered opens the drawer on nothing,
     * which reads as a guide that failed to load rather than as a filter.
     *
     * `sections[0]` is optional under `noUncheckedIndexedAccess`, and the check
     * is kept rather than asserted away: an empty guide should open an empty
     * drawer, not throw on the way in.
     */
    const covering = guideSectionIdForPath(pathname);
    const here = sections.some((section) => section.id === covering)
      ? covering
      : sections[0]?.id;
    setExpanded(here ? [here] : []);
    setQuery('');
    setOpen(true);
  }

  /*
   * Focus goes into the sheet on open and back to the trigger on close. The
   * panel itself takes it, rather than the filter box, so the dialog is
   * announced by name before a text field steals the introduction.
   */
  useEffect(() => {
    if (!open) return;
    // Copied into a local before the cleanup closes over it: reading
    // `triggerRef.current` inside the cleanup reads it at teardown, which is a
    // different moment and a different element if the tree moved under us.
    const trigger = triggerRef.current;
    panelRef.current?.focus();
    return () => trigger?.focus();
  }, [open]);

  /*
   * Put focus back when a control drops it.
   *
   * Collapsing a section removes the focused header's siblings and, when the
   * filter empties the list, the focused element itself — focus lands on
   * `<body>` and nothing catches it. `onKeyDown` below is a REACT handler on
   * the panel, so with focus on the body it never sees a keystroke: Escape
   * stops closing the sheet and Tab walks into the page behind the scrim.
   *
   * ⚠️ MUST be `focusout`, not `focusin`. When the focused element is removed,
   * focus lands on the body and `focusout` fires, but **no `focusin` follows** —
   * the body does not "receive" focus, so a listener for it never runs.
   *
   * The check is deferred a tick because during `focusout` the active element
   * is still the one LOSING focus, so testing it inline is always false.
   *
   * The condition is deliberately `body` and nothing else. A general "focus
   * left the panel, pull it back" rule would fight anything legitimately
   * portalled out of this subtree; the body is never a legitimate place for
   * focus to sit while a dialog is up.
   */
  useEffect(() => {
    if (!open) return;
    let pending = 0;
    function recover() {
      window.clearTimeout(pending);
      pending = window.setTimeout(() => {
        if (document.activeElement === document.body) panelRef.current?.focus();
      }, 0);
    }
    document.addEventListener('focusout', recover);
    return () => {
      // Without clearing this, a focusout fired while the sheet is closing
      // would land after the trigger gets focus back and yank it into a panel
      // that is no longer on screen.
      window.clearTimeout(pending);
      document.removeEventListener('focusout', recover);
    };
  }, [open]);

  const folded = query.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!folded) return sections;
    return sections.filter((section) => guideSearchText(section).includes(folded));
  }, [folded, sections]);

  /*
   * While filtering, every surviving section is open — DERIVED, never written
   * into `expanded`. A filter that returned a list of collapsed titles would
   * hide the very line somebody searched for, and folding the expansion into
   * state would leave all of them open when the box is cleared.
   */
  const isExpanded = (id: string) => folded !== '' || expanded.includes(id);

  function toggle(id: string) {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) return;

    event.stopPropagation();

    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    const first = items.at(0);
    const last = items.at(-1);
    if (!first || !last) return;

    const active = document.activeElement;
    // The panel itself holds focus on open and is not in `items`, so "outside
    // the ring" has to wrap in both directions — otherwise the very first Tab
    // walks straight out into the page behind.
    const inRing = active instanceof HTMLElement && items.includes(active);

    if (event.shiftKey && (active === first || !inRing)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inRing)) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        iconOnly
        onClick={openDrawer}
        aria-label="Open the SEED EMERGE guide"
        aria-expanded={open}
        leadingIcon={<QuestionIcon />}
      />

      {open &&
        createPortal(
          /*
           * `z-50` is the OVERLAYS rung of the ladder at the top of
           * `src/index.css` — the same rung as `Modal` and `ActivityDrawer`. It
           * has to clear the sticky top bar (`z-30`, app chrome) that the
           * trigger lives in, and it is portalled to the body so it escapes
           * that bar's stacking context rather than being painted as part of
           * it.
           */
          <div className="fixed inset-0 z-50 flex justify-end">
            <div
              aria-hidden="true"
              onClick={close}
              className="absolute inset-0 bg-scrim backdrop-blur-[2px]"
            />

            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              tabIndex={-1}
              onKeyDown={onKeyDown}
              className={cx(
                'relative flex h-full w-full max-w-full flex-col border-l border-border bg-surface shadow-lg',
                // Full width on a phone — at 320px the sheet IS the screen —
                // and a fixed reading width from `sm` up.
                'sm:w-[30rem]',
                'focus:outline-none',
              )}
            >
              <header className="flex items-start gap-3 border-b border-border px-4 py-3">
                <h2 id={titleId} className="m-0 min-w-0 flex-1 text-sm font-semibold text-fg">
                  SEED EMERGE guide
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={close}
                  aria-label="Close the guide"
                  leadingIcon={<CloseIcon />}
                />
              </header>

              {/* Outside the scroller, so the filter is never below the fold. */}
              <div className="border-b border-border bg-surface-sunken px-4 py-2">
                <label htmlFor={filterId} className="sr-only">
                  Filter the guide
                </label>
                <Input
                  id={filterId}
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Filter the guide"
                  leadingIcon={<SearchIcon />}
                />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {matches.length === 0 ? (
                  <EmptyState
                    title="Nothing in the guide matches that"
                    description="Try a shorter word, or clear the filter to read the whole guide."
                    action={
                      <Button variant="secondary" size="sm" onClick={() => setQuery('')}>
                        Clear the filter
                      </Button>
                    }
                  />
                ) : (
                  matches.map((section) => (
                    <GuideSectionView
                      key={section.id}
                      section={section}
                      open={isExpanded(section.id)}
                      onToggle={() => toggle(section.id)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
